import React, { useState } from 'react';
import { motion } from 'framer-motion';

const StadoValueProps = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const values = [
    {
      title: "Reliability",
      desc: "Single source of truth for all HR and payroll data.",
      icon: <path d="M12 2l9 4v6c0 5-3.8 9.4-9 10-5.2-.6-9-5-9-10V6l9-4z"/>,
      delay: 0.1
    },
    {
      title: "Transparency",
      desc: "Employees and admins access the same real-time records.",
      icon: <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>,
      delay: 0.2
    },
    {
      title: "Speed",
      desc: "Automated attendance dramatically reduces payroll time.",
      icon: <path d="M13 2L3 14h7l-1 8L21 10h-7l-1-8z"/>,
      delay: 0.3
    },
    {
      title: "Localization",
      desc: "Built for Indian payroll laws & business workflows.",
      icon: <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>,
      delay: 0.4
    }
  ];

  return (
    <section className="value-viewport">
      <style>{`
        .value-viewport {
          padding: 100px 5%;
          background: #050508;
          font-family: 'Inter', sans-serif;
        }

        .container { max-width: 1300px; margin: 0 auto; }

        .header { text-align: center; margin-bottom: 80px; }
        .header h2 { 
          font-size: 28px; 
          font-weight: 900; 
          color: white; 
          margin-bottom: 15px;
        }
        .pink-gradient {
          background: linear-gradient(90deg, #ff3b69, #ff914d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .header p { 
          color: #94a3b8; 
          font-size: 18px; 
          max-width: 600px;
          margin: 0 auto;
        }

        .value-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr); /* Desktop */
          gap: 30px;
        }

        @media (max-width: 1024px) {
          .value-grid {
            grid-template-columns: repeat(2, 1fr); /* Tablet */
          }
        }

        @media (max-width: 600px) {
          .value-grid {
            grid-template-columns: 1fr; /* Mobile: one by one */
            gap: 24px;
          }
          .value-viewport {
            padding: 60px 16px;
          }
        }

        .value-card {
          width: 100%;
          max-width: 420px;   /* 🔥 Same size like desktop */
          margin: 0 auto;     /* Center on mobile */
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 28px;
          padding: 48px 32px;
          text-align: center;
          position: relative;
          backdrop-filter: blur(12px);
          transition: all 0.4s ease;
        }

        .value-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          background: linear-gradient(135deg, #ff3b69, #ff914d);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        @media (hover: hover) {
          .value-card:hover {
            transform: translateY(-8px);
            background: rgba(255, 255, 255, 0.06);
          }
          .value-card:hover::before { opacity: 1; }
          .value-card:hover .icon-box {
            background: linear-gradient(135deg, #ff3b69, #ff914d);
            transform: scale(1.1) rotate(8deg);
            box-shadow: 0 12px 30px rgba(255, 59, 105, 0.4);
          }
          .value-card:hover .icon-box svg { fill: white; }
        }

        .value-card.mobile-active {
          transform: translateY(-6px);
          background: rgba(255, 255, 255, 0.06);
        }
        .value-card.mobile-active::before { opacity: 1; }
        .value-card.mobile-active .icon-box {
          background: linear-gradient(135deg, #ff3b69, #ff914d);
          transform: scale(1.1) rotate(6deg);
          box-shadow: 0 12px 30px rgba(255, 59, 105, 0.4);
        }
        .value-card.mobile-active .icon-box svg { fill: white; }

        .icon-box {
          width: 80px;
          height: 80px;
          margin: 0 auto 26px;
          background: rgba(255, 59, 105, 0.08);
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(255, 59, 105, 0.2);
          transition: all 0.4s ease;
        }

        .icon-box svg { width: 40%; height: 40%; fill: #ff3b69; }

        .value-card h3 { color: #ff914d; font-size: 22px; margin-bottom: 12px; font-weight: 700; }
        .value-card p { color: #94a3b8; font-size: 15px; line-height: 1.6; }
      `}</style>

      <div className="container">
        <div className="header">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            VALUE <span className="pink-gradient">PROPOSITION</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
            Empowering modern businesses with an all-in-one payroll ecosystem.
          </motion.p>
        </div>

        <div className="value-grid">
          {values.map((v, i) => (
            <motion.div
              key={i}
              className={`value-card ${activeIndex === i ? "mobile-active" : ""}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              onViewportEnter={() => setActiveIndex(i)}
              onClick={() => setActiveIndex(i)}
              transition={{ duration: 0.5, delay: v.delay, ease: "easeOut" }}
              viewport={{ once: false, margin: "-40px" }}
            >
              <div className="icon-box">
                <svg viewBox="0 0 24 24">{v.icon}</svg>
              </div>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StadoValueProps;
